import { storageImage, storageSrcSet } from "@/lib/imageUrl";
import { Link, useParams, useNavigate, useLocation } from "@/lib/router-compat";
import { useState, useEffect, useMemo } from "react";
import { useProjects } from "@/hooks/useProjects";
import { useIndustries } from "@/hooks/useIndustries";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Camera, Video, Share2, ArrowRight, Grid3x3, Filter, Globe } from "lucide-react";
import { LazyCoverImage } from "@/components/LazyCoverImage";
import { Skeleton } from "@/components/ui/skeleton";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import heroImage from "@/assets/hero-bg.jpg";
import { SEOHead } from "@/components/SEOHead";
import { BookingDialog } from "@/components/BookingDialogLazy";
import { toSlug } from "@/lib/utils";
import { BreadcrumbSchema } from "@/components/StructuredData";
import { MarketFilter } from "@/components/MarketFilter";
import { LOAD_ERROR_MESSAGE } from "@/lib/publicQueries";
const Portfolio = () => {
  const { industry: industryParam } = useParams<{ industry: string }>();
  const navigate = useNavigate();
  const location = useLocation();
    const [activeCategory, setActiveCategory] = useState<"all" | "photography" | "videography" | "social-media">("all");
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [selectedMarket, setSelectedMarket] = useState<'de' | 'en'>('de');
  const { data: projects = [], isLoading: projectsLoading, isError: projectsError } = useProjects({ publicOnly: true });
  const { data: industries = [], isLoading: industriesLoading, isError: industriesError } = useIndustries();
  // Initialise from the URL + prefetched industries so server and browser render the same view
  const [selectedIndustry, setSelectedIndustry] = useState<string | null>(() => {
    if (!industryParam) return null;
    const match = industries.find((i) => toSlug(i.name) === industryParam.toLowerCase());
    return match ? match.name : null;
  });

  // Read market from URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const marketParam = params.get('market') as 'de' | 'en' | null;
    if (marketParam && ['de', 'en'].includes(marketParam)) {
      setSelectedMarket(marketParam);
    }
  }, [location.search]);

  useEffect(() => {
    if (industryParam) {
      const slug = industryParam.toLowerCase();
      if (industries.length > 0) {
        const match = industries.find((i) => toSlug(i.name) === slug);
        setSelectedIndustry(match ? match.name : null);
      }
    } else {
      setSelectedIndustry(null);
    }
  }, [industryParam, industries]);

  const categoryIcons = {
    photography: Camera,
    videography: Video,
    "social-media": Share2,
  };

  const categoryLabels = {
    photography: "Fotografie",
    videography: "Videoproduktion",
    "social-media": "Social Media",
  };

  const handleMarketChange = (market: 'de' | 'en') => {
    setSelectedMarket(market);
    if (selectedIndustry) {
      // If an industry is selected, keep it and just update the market
      navigate(`/portfolio/industry/${toSlug(selectedIndustry)}?market=${market}`);
    } else {
      // If no industry selected, go to main portfolio with new market
      navigate(`/portfolio?market=${market}`);
      setActiveCategory("all");
    }
  };

  const handleCategoryChange = (category: "all" | "photography" | "videography" | "social-media") => {
    setActiveCategory(category);
  };

  // Filter projects by market, industry and category
  const filteredProjects = projects.filter(p => {
    const matchesMarket = p.language === selectedMarket;
    const matchesIndustry = !selectedIndustry || p.industry === selectedIndustry;
    const matchesCategory = activeCategory === "all" || p.category === activeCategory;
    return matchesMarket && matchesIndustry && matchesCategory;
  });

  // Dynamic SEO based on selected industry and market
  const seoData = useMemo(() => {
    const marketSuffix = selectedMarket === 'en' ? ' (International)' : '';
    
    const marketDescription = selectedMarket === 'en'
      ? 'für internationale Märkte (inkl. Englisch, Arabisch, etc.)'
      : 'für den deutschen und DACH-Markt';

    if (selectedIndustry) {
      const slug = toSlug(selectedIndustry);
      return {
        title: `${selectedIndustry} Portfolio${marketSuffix} - Fotografie & Videoproduktion | Clicks DMP`,
        description: `Entdecken Sie unsere professionellen ${selectedIndustry}-Projekte ${marketDescription}. Fotografie, Videoproduktion und Social Media Content aus Mannheim.`,
        canonical: `https://clicksdmp.com/portfolio/industry/${slug}?market=${selectedMarket}`,
      };
    }
    return {
      title: `Portfolio${marketSuffix} - Fotografie & Videoproduktion | Clicks DMP Mannheim`,
      description: `Entdecken Sie unser Portfolio professioneller Fotografie, Videoproduktion und Social Media Content ${marketDescription}. Referenzen aus Mannheim, Heidelberg und Ludwigshafen.`,
      canonical: `https://clicksdmp.com/portfolio?market=${selectedMarket}`,
    };
  }, [selectedIndustry, selectedMarket]);

  // Breadcrumb structured data
  const breadcrumbItems = useMemo(() => {
    const items = [
      { name: "Startseite", url: "https://clicksdmp.com/" },
      { name: "Portfolio", url: "https://clicksdmp.com/portfolio" },
    ];
    if (selectedIndustry) {
      items.push({
        name: selectedIndustry,
        url: `https://clicksdmp.com/portfolio/industry/${toSlug(selectedIndustry)}`,
      });
    }
    return items;
  }, [selectedIndustry]);

  return (
    <>
      <SEOHead
        title={seoData.title}
        description={seoData.description}
        canonical={seoData.canonical}
        schema={<BreadcrumbSchema items={breadcrumbItems} />}
      />
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden mt-20">{/*mt-20 for header spacing*/}
          {/* Background Image */}
          <div className="absolute inset-0">
            <img 
              src={heroImage} 
              alt="Portfolio Hero" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background" />
          </div>

          {/* Hero Content */}
          <div className="relative z-10 container-custom text-center section-padding animate-fade-in">
            <Badge className="mb-6 bg-primary/20 text-primary border-primary/30 hover:bg-primary/30">
              <Grid3x3 className="w-3 h-3 mr-2" />
              Portfolio
            </Badge>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold max-w-4xl mx-auto">
              Unsere <span className="text-gradient">Arbeiten</span>
            </h1>
            <p className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 max-w-4xl mx-auto">
              sprechen für sich
            </p>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              {selectedMarket === 'de' 
                ? 'Entdecken Sie unsere deutschen Projekte aus verschiedenen Branchen und Bereichen.'
                : 'Entdecken Sie unsere internationalen Projekte aus verschiedenen Branchen und Bereichen.'}
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button size="lg" className="gap-2 group" asChild>
                <a href="#market">
                  Projekte entdecken
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
              <Button size="lg" variant="outline" onClick={() => setBookingDialogOpen(true)}>
                Projekt anfragen
              </Button>
            </div>
          </div>
        </section>

        {/* Market Selector Section */}
        <section id="market" className="section-padding-sm bg-secondary/5 border-b border-border">
          <div className="container-custom">
            <div className="flex flex-col items-center gap-6">
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm mb-3">
                  <Globe className="w-4 h-4" />
                  <span>Projekte nach Zielmarkt</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold mb-2">Für welchen Markt interessieren Sie sich?</h2>
                <p className="text-muted-foreground max-w-2xl">
                  Entdecken Sie unsere Arbeiten für Kunden aus verschiedenen Märkten und Regionen
                </p>
              </div>
              
              <div className="flex flex-wrap gap-3 justify-center">
                <Button
                  variant={selectedMarket === 'de' ? 'default' : 'outline'}
                  size="lg"
                  onClick={() => handleMarketChange('de')}
                  className="gap-2 min-w-[180px]"
                >
                  <span className="text-lg">🇩🇪</span>
                  <span>DACH-Region</span>
                </Button>
                
                <Button
                  variant={selectedMarket === 'en' ? 'default' : 'outline'}
                  size="lg"
                  onClick={() => handleMarketChange('en')}
                  className="gap-2 min-w-[180px]"
                >
                  <span className="text-lg">🇬🇧</span>
                  <span>International</span>
                </Button>
                
              </div>
              
              <p className="text-sm text-muted-foreground text-center">
                {selectedMarket === 'de' && 'Zeige Projekte für den deutschen & DACH-Markt'}
                {selectedMarket === 'en' && 'Zeige internationale Projekte (inkl. Englisch, Arabisch, etc.)'}
                <span className="mx-2">•</span>
                <span className="font-semibold">{filteredProjects.length}</span>
                {' '}{filteredProjects.length === 1 ? 'Projekt' : 'Projekte'}
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section id="industries" className="section-padding">
          <div className="container-custom">
            {!selectedIndustry ? (
              /* Industry Selection View */
              <>
                <div className="text-center mb-16 animate-fade-in">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-4">
                    <Filter className="w-4 h-4" />
                    <span className="text-sm font-medium">Schritt 1: Branche wählen</span>
                  </div>
                  <h2 className="text-3xl md:text-5xl font-bold mb-6">
                    Wählen Sie Ihre <span className="text-gradient">Branche</span>
                  </h2>
                  <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                    Wir haben mit unterschiedlichsten Branchen gearbeitet. Wählen Sie eine Branche, um unsere spezifischen Projekte zu sehen.
                  </p>
                </div>

                {industriesError && industries.length === 0 ? (
                  <p role="alert" className="text-center text-muted-foreground py-12">{LOAD_ERROR_MESSAGE}</p>
                ) : industriesLoading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} className="rounded-2xl overflow-hidden">
                        <Skeleton className="aspect-[4/3] w-full" />
                        <div className="p-6 space-y-3">
                          <Skeleton className="h-6 w-3/4" />
                          <Skeleton className="h-4 w-full" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {industries.map((industry, index) => (
                      <Link
                        key={index}
                        to={`/portfolio/industry/${toSlug(industry.name)}${new URLSearchParams(location.search).get("market") ? `?market=${selectedMarket}` : ""}`}
                        onClick={() => { setSelectedIndustry(industry.name); setActiveCategory("all"); }}
                        className="group block text-left animate-fade-in"
                        style={{ animationDelay: `${index * 50}ms` }}
                      >
                        <div className="relative overflow-hidden rounded-2xl bg-card border border-border transition-all duration-500 hover:border-primary hover:shadow-xl hover:shadow-primary/20 hover:-translate-y-2">
                          <div className="relative aspect-[4/3] overflow-hidden">
                            <img
                          src={storageImage(industry.coverImage, 800)}
                          srcSet={storageSrcSet(industry.coverImage)}
                          sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
                          alt={industry.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          loading="lazy"
                          decoding="async"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />
                            
                            {/* Overlay Content */}
                            <div className="absolute bottom-0 left-0 right-0 p-6">
                              <h3 className="text-2xl font-bold mb-2 text-foreground group-hover:text-primary transition-colors">
                                {industry.name}
                              </h3>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground group-hover:text-primary transition-colors">
                                <span>Projekte ansehen</span>
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </>
            ) : (
              /* Projects View with Filters */
              <>
                {/* Filter Header */}
                <div className="mb-12 animate-fade-in">
                  <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                    <div className="flex items-center gap-3">
                      <Button
                        asChild
                        variant="outline"
                        size="sm"
                        className="gap-2 hover:bg-destructive/10 hover:text-destructive hover:border-destructive"
                      >
                        <Link
                          to={`/portfolio?market=${selectedMarket}`}
                          onClick={() => { setSelectedIndustry(null); setActiveCategory("all"); }}
                        >
                          <ArrowLeft className="w-4 h-4" />
                          Zurück zu Branchen
                        </Link>
                      </Button>
                      <Badge variant="outline" className="text-base py-2 px-4">
                        {selectedIndustry}
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {filteredProjects.length} {filteredProjects.length === 1 ? 'Projekt' : 'Projekte'}
                    </div>
                  </div>

                  <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-4">
                      <Filter className="w-4 h-4" />
                      <span className="text-sm font-medium">Schritt 2: Kategorie filtern</span>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">
                      Projekte für <span className="text-gradient">{selectedIndustry}</span>
                    </h2>
                    <p className="text-muted-foreground max-w-2xl mx-auto">
                      Filtern Sie nach Kategorie, um spezifische Arbeiten zu entdecken
                    </p>
                  </div>

                  {/* Category Filter Chips */}
                  <div className="flex flex-wrap gap-3 justify-center">
                    <Button
                      variant={activeCategory === "all" ? "default" : "outline"}
                      size="lg"
                      onClick={() => handleCategoryChange("all")}
                      className="gap-2"
                    >
                      Alle Kategorien
                    </Button>
                    {(Object.keys(categoryIcons) as Array<keyof typeof categoryIcons>).map((category) => {
                      const Icon = categoryIcons[category];
                      return (
                        <Button
                          key={category}
                          variant={activeCategory === category ? "default" : "outline"}
                          size="lg"
                          onClick={() => handleCategoryChange(category)}
                          className="gap-2"
                        >
                          <Icon className="w-4 h-4" />
                          {categoryLabels[category]}
                        </Button>
                      );
                    })}
                  </div>
                  
                  {/* Market Filter - Added for industry view */}
                  <div className="mt-8">
                    <MarketFilter 
                      selectedMarket={selectedMarket}
                      onMarketChange={handleMarketChange}
                      projectCount={filteredProjects.length}
                      className="mb-8"
                    />
                  </div>
                </div>

                {/* Projects Grid */}
                {projectsError && projects.length === 0 ? (
                  <p role="alert" className="text-center text-muted-foreground py-12">{LOAD_ERROR_MESSAGE}</p>
                ) : projectsLoading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} className="rounded-2xl overflow-hidden">
                        <Skeleton className="aspect-[4/3] w-full" />
                        <div className="p-6 space-y-3">
                          <Skeleton className="h-6 w-3/4" />
                          <Skeleton className="h-4 w-full" />
                          <Skeleton className="h-4 w-2/3" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : filteredProjects.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredProjects.map((project, index) => {
                      const Icon = categoryIcons[project.category];
                      return (
                        <Link
                          key={project.id}
                          to={`/portfolio/${project.slug}`}
                          className="group animate-fade-in"
                          style={{ animationDelay: `${index * 50}ms` }}
                        >
                          <div className="relative overflow-hidden rounded-2xl bg-card border border-border transition-all duration-500 hover:border-primary hover:shadow-xl hover:shadow-primary/20 hover:-translate-y-2 h-full">
                            {/* Project Image */}
                            <div className="relative aspect-[4/3] overflow-hidden">
                              <LazyCoverImage
                                projectId={project.id}
                                category={project.category}
                                title={project.title}
                                className="w-full h-full group-hover:scale-110 transition-transform duration-700"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent opacity-60 group-hover:opacity-80 transition-opacity" />
                              
                              {/* Category Badge */}
                              <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-background/90 backdrop-blur-xs border border-border">
                                <Icon className="w-4 h-4 text-primary" />
                                <span className="text-xs font-medium">{categoryLabels[project.category]}</span>
                              </div>
                            </div>

                            {/* Project Info */}
                            <div className="p-6">
                              <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors line-clamp-1">
                                {project.title}
                              </h3>
                              <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
                                {project.description}
                              </p>
                              
                              <div className="flex items-center justify-between">
                                {project.client && (
                                  <p className="text-xs text-muted-foreground">
                                    {project.client}
                                  </p>
                                )}
                                <div className="flex items-center gap-1 text-primary text-sm font-medium ml-auto">
                                  <span>Ansehen</span>
                                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </div>
                              </div>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                      <Grid3x3 className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">Keine Projekte gefunden</h3>
                    <p className="text-muted-foreground mb-6">
                      Für diese Kombination aus Branche und Kategorie gibt es derzeit keine Projekte.
                    </p>
                    <Button variant="outline" onClick={() => setActiveCategory("all")}>
                      Alle Kategorien anzeigen
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* CTA Section */}
        {!selectedIndustry && (
          <section className="section-padding bg-gradient-to-b from-background to-primary/5 border-t border-border">
            <div className="container-custom text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Bereit für Ihr eigenes <span className="text-gradient">Projekt</span>?
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8">
                Lassen Sie uns gemeinsam etwas Großartiges schaffen. Kontaktieren Sie uns für ein unverbindliches Beratungsgespräch.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Button size="lg" className="gap-2 group" asChild>
                  <a href="/#contact">
                    Jetzt Projekt starten
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </a>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link to="/">
                    Mehr über uns erfahren
                  </Link>
                </Button>
              </div>
            </div>
          </section>
        )}

        <Footer />
      </div>
      
      <BookingDialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen} />
    </>
  );
};

export default Portfolio;
